/* eslint-disable react/display-name */
import { childrenSelector } from '@smartsheet-dataflow/formjs';
import { EntityId } from '@smartsheet-dataflow/formjs-react';
import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  addInputActionCreator,
  removeInputActionCreator,
} from '../../../services/forms/BridgeSchemaEnhancer';
import {
  isOpenSelector,
  setIsOpenAction,
} from '../../../services/forms/features';
import { PartialBy } from '../../../utils/types';

interface WithFormJSChildren {
  entities: string[];
  addNewItem: (inputId: string) => void;
  removeItem: (inputId: string) => void;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

export const withFormJSChildren =
  <P extends EntityId & WithFormJSChildren>(
    Component: React.ComponentType<P>
  ) =>
  (
    props: PartialBy<
      P,
      'entities' | 'addNewItem' | 'removeItem' | 'isOpen' | 'setIsOpen'
    >
  ) => {
    const entities = useSelector(childrenSelector(props.id));

    const dispatch = useDispatch();
    const addInputAction = addInputActionCreator(dispatch);
    const removeInputAction = removeInputActionCreator(dispatch);
    const setIsOpen = setIsOpenAction(dispatch);
    const addNewItem = useCallback(
      (inputId: string) => addInputAction({ parentId: props.id, inputId }),
      [addInputAction, props.id]
    );
    const removeItem = useCallback(
      (inputId: string) => removeInputAction({ parentId: props.id, inputId }),
      [props.id, removeInputAction]
    );

    const isOpen = useSelector(isOpenSelector(props.id));
    const setIsOpenHandler = useCallback(
      (isOpen: boolean) => setIsOpen({ inputId: props.id, isOpen }),
      [props.id, setIsOpen]
    );

    return (
      <Component
        {...(props as P)}
        entities={entities}
        addNewItem={addNewItem}
        removeItem={removeItem}
        isOpen={isOpen}
        setIsOpen={setIsOpenHandler}
      />
    );
  };
